import BasicMultiSelect from 'app/ui_v2/form-fields/basic-fields/select/BasicMultiSelect';
import BasicSelect from 'app/ui_v2/form-fields/basic-fields/select/BasicSelect';
import BasicTextInput from 'app/ui_v2/form-fields/basic-fields/text-input/TextInput';
import FormWrapper from 'app/ui_v2/form-fields/form-wrapper/FormWrapper';
import RangeDatepicker from 'app/ui_v2/form-fields/range-datepicker/RangeDatepicker';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Card, CardContent, Grid } from '@mui/material';

import AgencyPicker from '../../common/agency-picker/AgencyPicker';
import { ALL_OPT, defaultValues, FormValues, getValidationSchema, PENDING_REQUESTS, PROJECT_TYPES, SEARCH_TYPES, STATUS_FILTERS, SWO_OPTS } from './validation-schema';

export interface BookingsSearchFormProps {
  isLoading: boolean;
  onFormSubmit: (values: FormValues) => void;
  onFormChange: (values: FormValues) => void;
  defaults?: FormValues;
  ifReset: number;
}

export default function BookingsSearchForm(props: BookingsSearchFormProps) {
  const { onFormSubmit, onFormChange, isLoading, defaults, ifReset } = props;

  const [resetDatepicker, setResetDatepicker] = useState<number>(0);

  const { handleSubmit, control, watch, reset, setValue } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { ...defaultValues, ...(defaults && { ...defaults }) },
    resolver: yupResolver(getValidationSchema()),
  });

  const onSubmit = (data: FormValues) => {
    onFormSubmit(data);
  };

  const handleChange = () => {
    const data = watch();

    if (data.searchBy !== '' && data.query === '') {
      return;
    }
    onFormChange(data);
  };

  const handleReset = (isSilent: boolean = false) => {
    reset(defaultValues);
    setValue('dateTo', null);
    setValue('dateFrom', null);
    setResetDatepicker((v) => v + 1);
    if (!isSilent) {
      setTimeout(() => {
        handleChange();
      }, 1);
    }
  };

  useEffect(() => {
    if (ifReset > 0) {
      handleReset(true);
    }
  }, [ifReset]);

  return (
    <Card>
      <CardContent>
        <FormWrapper<FormValues>
          onChangeCallback={handleChange}
          onSubmitCallback={onSubmit}
          handleSubmit={handleSubmit}
        >
          <>
            <Grid container columnSpacing={4} spacing={4}>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicSelect collection={SEARCH_TYPES} label="Search By" inputName="searchBy" control={control} />
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicTextInput label="Search" inputName="query" control={control} />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicSelect collection={PROJECT_TYPES} label="Project Type" inputName="type" control={control} />
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3}>
                <BasicMultiSelect
                  collection={STATUS_FILTERS}
                  label="Status"
                  inputName="states"
                  control={control}
                  isNested={true}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3}>
                <AgencyPicker control={control} fieldName="agencyCompanyId" fieldLabel="Agency" />
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3}>
                <BasicSelect
                  collection={[ALL_OPT, ...SWO_OPTS]}
                  label="Has Hold/Lock"
                  inputName="hasSwo"
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={3} lg={3}>
                <BasicSelect
                  collection={[ALL_OPT, ...PENDING_REQUESTS]}
                  label="Pending Requests"
                  inputName="pendingRequests"
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={10} lg={6}>
                <RangeDatepicker control={control} reset={resetDatepicker} />
              </Grid>

              <Grid item xs>
                <Box display="flex" justifyContent="flex-end" mt={2.5}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    disabled={isLoading}
                    disableElevation
                    onClick={() => {
                      handleReset();
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        </FormWrapper>
      </CardContent>
    </Card>
  );
}
